import { supabase } from '../../shared/supabase/supabaseClient';
import {
  createWalkInAppointment,
  getAppointmentById,
  listAppointmentsByQuoteRequestId,
} from '../../appointments/infra/appointmentsRepo';
import type { Appointment } from '../../appointments/domain/types';
import { computeSessionProgress } from '../domain/sessionProgress';
import { quoteServiceDisplayName } from '../domain/quoteDisplay';
import type {
  QuoteListFilter,
  ServiceQuoteMessage,
  ServiceQuotePlanPaymentMode,
  ServiceQuotePricingMode,
  ServiceQuoteRequest,
  ServiceQuoteStatus,
} from '../domain/types';

const CLOSED: ServiceQuoteStatus[] = ['completed', 'declined', 'expired', 'cancelled'];
const UNREAD_STATUSES: ServiceQuoteStatus[] = ['requested', 'needs_info'];

/** Maps a Supabase row to a ServiceQuoteRequest domain object. Exported for unit testing. */
export function mapQuote(row: Record<string, unknown>): ServiceQuoteRequest {
  return {
    id: String(row.id),
    organization_id: String(row.organization_id),
    branch_id: String(row.branch_id),
    customer_user_id: String(row.customer_user_id),
    customer_name: String(row.customer_name),
    customer_phone: (row.customer_phone as string | null) ?? null,
    customer_email: (row.customer_email as string | null) ?? null,
    product_id: String(row.product_id),
    note: String(row.note ?? ''),
    status: row.status as ServiceQuoteStatus,
    pricing_mode: (row.pricing_mode as ServiceQuotePricingMode | null) ?? null,
    price_per_session: row.price_per_session != null ? Number(row.price_per_session) : null,
    quoted_total: row.quoted_total != null ? Number(row.quoted_total) : null,
    session_duration_minutes:
      row.session_duration_minutes != null ? Number(row.session_duration_minutes) : null,
    estimated_sessions: Number(row.estimated_sessions ?? 1),
    quote_note: (row.quote_note as string | null) ?? null,
    plan_payment_mode: (row.plan_payment_mode as ServiceQuotePlanPaymentMode | null) ?? 'inherit',
    plan_deposit_amount: row.plan_deposit_amount != null ? Number(row.plan_deposit_amount) : null,
    quoted_at: (row.quoted_at as string | null) ?? null,
    expires_at: (row.expires_at as string | null) ?? null,
    accepted_at: (row.accepted_at as string | null) ?? null,
    accepted_by: (row.accepted_by as 'customer' | 'store' | null) ?? null,
    schedule_without_accept: Boolean(row.schedule_without_accept),
    decline_reason: (row.decline_reason as string | null) ?? null,
    evaluation_appointment_id: (row.evaluation_appointment_id as string | null) ?? null,
    photo_urls: Array.isArray(row.photo_urls) ? (row.photo_urls as string[]) : [],
    needs_photo: Boolean(row.needs_photo),
    read_by_store_at: (row.read_by_store_at as string | null) ?? null,
    read_by_customer_at: (row.read_by_customer_at as string | null) ?? null,
    created_at: String(row.created_at),
    updated_at: String(row.updated_at),
  };
}

/** Checks if a quote matches a list filter. Exported for unit testing. */
export function matchesFilter(q: ServiceQuoteRequest, filter: QuoteListFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'unread') {
    return !q.read_by_store_at && UNREAD_STATUSES.includes(q.status);
  }
  if (filter === 'requested') {
    return q.status === 'requested' || q.status === 'needs_info' || q.status === 'visit_proposed' || q.status === 'visit_scheduled';
  }
  if (filter === 'quoted') return q.status === 'quoted';
  if (filter === 'accepted') return q.status === 'accepted' || q.status === 'scheduled';
  if (filter === 'closed') return CLOSED.includes(q.status);
  return true;
}

function statusArrayForFilter(filter: QuoteListFilter): string[] | null {
  if (filter === 'all' || filter === 'unread') return null;
  if (filter === 'quoted') return ['quoted'];
  if (filter === 'accepted') return ['accepted', 'scheduled'];
  if (filter === 'requested') return ['requested', 'needs_info', 'visit_proposed', 'visit_scheduled'];
  if (filter === 'closed') return ['completed', 'declined', 'expired', 'cancelled'];
  return null;
}

export async function listOrgQuotes(
  branchId?: string | null,
  filter: QuoteListFilter = 'all',
): Promise<ServiceQuoteRequest[]> {
  const statuses = statusArrayForFilter(filter);

  const { data, error } = await supabase.rpc('list_org_service_quotes_v2', {
    p_branch_id: branchId ?? null,
    p_statuses: statuses ?? [],
  });
  if (error) throw new Error(error.message);

  const rows = ((data ?? []) as Record<string, unknown>[]).map(mapQuote);
  const productIds = [...new Set(rows.map((q) => q.product_id).filter(Boolean))];
  const productById = new Map<string, { name: string; isMisc: boolean }>();
  if (productIds.length > 0) {
    const { data: products } = await supabase
      .from('products')
      .select('id, name, is_misc')
      .in('id', productIds);
    for (const p of (products as { id: string; name: string; is_misc: boolean | null }[] | null) ??
      []) {
      productById.set(p.id, { name: p.name, isMisc: p.is_misc === true });
    }
  }

  return rows
    .map((q) => {
      const product = productById.get(q.product_id);
      return {
        ...q,
        // Display mapping: el producto misc del POS no expone su nombre interno.
        product_name: quoteServiceDisplayName(product?.name, product?.isMisc),
      };
    })
    .filter((q) => matchesFilter(q, filter));
}

export async function countUnreadQuotesForOrg(): Promise<number> {
  const { count, error } = await supabase
    .from('service_quote_requests')
    .select('id', { count: 'exact', head: true })
    .is('read_by_store_at', null)
    .in('status', UNREAD_STATUSES);
  if (error) throw new Error(error.message);
  return count ?? 0;
}

export async function getQuote(quoteId: string): Promise<ServiceQuoteRequest | null> {
  const { data, error } = await supabase
    .from('service_quote_requests')
    .select('*')
    .eq('id', quoteId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) return null;
  const quote = mapQuote(data as Record<string, unknown>);
  const { data: product } = await supabase
    .from('products')
    .select('name, is_misc')
    .eq('id', quote.product_id)
    .maybeSingle();
  const p = product as { name?: string; is_misc?: boolean | null } | null;
  return {
    ...quote,
    product_name: quoteServiceDisplayName(p?.name, p?.is_misc),
  };
}

export async function listMessages(quoteId: string): Promise<ServiceQuoteMessage[]> {
  const { data, error } = await supabase
    .from('service_quote_messages')
    .select('id, quote_request_id, author, body, attachment_urls, created_at')
    .eq('quote_request_id', quoteId)
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as ServiceQuoteMessage[];
}

export async function issueQuote(input: {
  quoteId: string;
  pricingMode: ServiceQuotePricingMode;
  price?: number | null;
  sessionDurationMinutes: number;
  estimatedSessions: number;
  quoteNote?: string | null;
  planPaymentMode?: ServiceQuotePlanPaymentMode;
  planDepositAmount?: number | null;
}): Promise<ServiceQuoteRequest> {
  const { data, error } = await supabase.rpc('issue_service_quote', {
    p_quote_id: input.quoteId,
    p_pricing_mode: input.pricingMode,
    p_price_per_session: input.pricingMode === 'per_session' ? input.price ?? null : null,
    p_quoted_total: input.pricingMode === 'total' ? input.price ?? null : null,
    p_session_duration_minutes: input.sessionDurationMinutes,
    p_estimated_sessions: input.estimatedSessions,
    p_quote_note: input.quoteNote ?? null,
    p_plan_payment_mode: input.planPaymentMode ?? 'inherit',
    p_plan_deposit_amount:
      input.planPaymentMode === 'deposit' ? input.planDepositAmount ?? null : null,
  });
  if (error) throw new Error(error.message);
  return mapQuote(data as Record<string, unknown>);
}

export async function acceptAsStore(quoteId: string): Promise<ServiceQuoteRequest> {
  const { data, error } = await supabase.rpc('accept_service_quote', {
    p_quote_id: quoteId,
    p_as_store: true,
  });
  if (error) throw new Error(error.message);
  return mapQuote(data as Record<string, unknown>);
}

export type SetQuoteStatus =
  | 'needs_info'
  | 'visit_proposed'
  | 'declined'
  | 'cancelled'
  | 'completed';

export async function setStatus(
  quoteId: string,
  status: SetQuoteStatus,
  opts?: { message?: string | null; needsPhoto?: boolean; declineReason?: string | null },
): Promise<ServiceQuoteRequest> {
  const { data, error } = await supabase.rpc('set_service_quote_status', {
    p_quote_id: quoteId,
    p_status: status,
    p_message: opts?.message ?? null,
    p_needs_photo: opts?.needsPhoto ?? false,
    p_decline_reason: opts?.declineReason ?? null,
  });
  if (error) throw new Error(error.message);
  return mapQuote(data as Record<string, unknown>);
}

export async function sendStoreMessage(quoteId: string, body: string): Promise<void> {
  const text = body.trim();
  if (!text) throw new Error('Mensaje vacío');

  const { error } = await supabase.rpc('reply_service_quote_store', {
    p_quote_id: quoteId,
    p_body: text,
  });
  if (error) throw new Error(error.message);
}

export async function markReadByStore(quoteId: string): Promise<void> {
  const { error } = await supabase.rpc('mark_quote_read_by_store', {
    p_quote_id: quoteId,
  });
  if (error) throw new Error(error.message);
}

export async function getQuotePhotoSignedUrl(path: string): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from('service-quote-images')
    .createSignedUrl(path, 3600);
  if (error) return null;
  return data?.signedUrl ?? null;
}

function quotePaymentModeForSession(
  quote: ServiceQuoteRequest,
  depositAlreadyCollected: boolean,
): { paymentMode: 'none' | 'deposit' | 'full'; paymentStatus: 'unpaid' | 'waived' } {
  const mode = quote.plan_payment_mode ?? 'inherit';
  if (mode === 'none' || mode === 'inherit') {
    return { paymentMode: 'none', paymentStatus: 'waived' };
  }
  if (mode === 'deposit') {
    if (depositAlreadyCollected) {
      return { paymentMode: 'none', paymentStatus: 'waived' };
    }
    return { paymentMode: 'deposit', paymentStatus: 'unpaid' };
  }
  return { paymentMode: 'full', paymentStatus: 'unpaid' };
}

/** Create one or more walk-in sessions linked to a quote plan. */
export async function createWalkInSessions(input: {
  quote: ServiceQuoteRequest;
  sessions: Array<{ resourceId: string; startsAt: string; sessionIndex: number }>;
}): Promise<Appointment[]> {
  const duration = input.quote.session_duration_minutes ?? 60;
  const amount =
    input.quote.pricing_mode === 'per_session'
      ? input.quote.price_per_session
      : input.quote.quoted_total != null && input.quote.estimated_sessions > 0
        ? input.quote.quoted_total / input.quote.estimated_sessions
        : null;

  const existing = await listAppointmentsByQuoteRequestId(input.quote.id);
  const existingIndexes = new Set(
    existing
      .filter((a) => a.sessionIndex != null && a.status !== 'cancelled' && a.status !== 'no_show')
      .map((a) => a.sessionIndex as number),
  );
  let nextIndex = 1;

  const depositCollected = existing.some(
    (a) => a.paymentStatus === 'deposit_paid' || a.paymentStatus === 'paid',
  );
  let depositAlreadyUsed = depositCollected;

  const created: Appointment[] = [];
  for (const s of input.sessions) {
    const starts = new Date(s.startsAt);
    const ends = new Date(starts.getTime() + duration * 60_000);
    let sessionIndex = s.sessionIndex;
    if (sessionIndex == null || sessionIndex <= 0) {
      while (existingIndexes.has(nextIndex)) nextIndex++;
      sessionIndex = nextIndex++;
    }
    existingIndexes.add(sessionIndex);

    const { paymentMode, paymentStatus } = quotePaymentModeForSession(
      input.quote,
      depositAlreadyUsed,
    );
    if (paymentMode === 'deposit') {
      depositAlreadyUsed = true;
    }

    const appt = await createWalkInAppointment({
      organizationId: input.quote.organization_id,
      branchId: input.quote.branch_id,
      resourceId: s.resourceId,
      productId: input.quote.product_id,
      customerName: input.quote.customer_name,
      customerPhone: input.quote.customer_phone,
      customerEmail: input.quote.customer_email,
      startsAt: starts.toISOString(),
      endsAt: ends.toISOString(),
      notes: input.quote.quote_note,
      amountTotal: amount,
      quoteRequestId: input.quote.id,
      sessionIndex,
      paymentMode,
      paymentStatus,
    });
    created.push(appt);
  }

  if (input.quote.status === 'accepted' || input.quote.status === 'quoted') {
    await supabase
      .from('service_quote_requests')
      .update({ status: 'scheduled', updated_at: new Date().toISOString() })
      .eq('id', input.quote.id)
      .in('status', ['accepted', 'quoted']);
  }

  return created;
}

/** Linked plan sessions for a quote (excludes evaluation appointment). */
export async function listQuoteAppointments(quoteId: string): Promise<Appointment[]> {
  return listAppointmentsByQuoteRequestId(quoteId);
}

export async function getQuoteEvaluationAppointment(
  quote: ServiceQuoteRequest,
): Promise<Appointment | null> {
  if (!quote.evaluation_appointment_id) return null;
  return getAppointmentById(quote.evaluation_appointment_id);
}

/**
 * Booked session counts (unique session_index in counted statuses) for list meta.
 * One query for all quote ids — no N+1.
 */
export async function countBookedSessionsByQuoteIds(
  quoteIds: string[],
): Promise<Map<string, number>> {
  const out = new Map<string, number>();
  if (quoteIds.length === 0) return out;

  const { data, error } = await supabase
    .from('appointments')
    .select('quote_request_id, session_index, status')
    .in('quote_request_id', quoteIds)
    .not('session_index', 'is', null);

  if (error) throw new Error(error.message);

  const byQuote = new Map<string, { sessionIndex: number | null; status: string }[]>();
  for (const row of (data ?? []) as Array<{
    quote_request_id: string;
    session_index: number | null;
    status: string;
  }>) {
    const list = byQuote.get(row.quote_request_id) ?? [];
    list.push({
      sessionIndex: row.session_index != null ? Number(row.session_index) : null,
      status: row.status,
    });
    byQuote.set(row.quote_request_id, list);
  }

  for (const id of quoteIds) {
    const appts = byQuote.get(id) ?? [];
    // estimated_sessions unused for count-only; total ignored
    out.set(id, computeSessionProgress(appts, 1).bookedCount);
  }
  return out;
}

// ============================================================
// H-23: Convert quote → work order
// ============================================================

/**
 * Converts a quoted/accepted service quote into a work order.
 * The quoted service becomes the first task in the OT.
 * Returns the new work order ID.
 */
export async function convertQuoteToWorkOrder(quoteId: string): Promise<string> {
  const quote = await getQuote(quoteId);
  if (!quote) throw new Error('Cotización no encontrada.');
  if (!['quoted', 'accepted'].includes(quote.status)) {
    throw new Error('Solo se pueden convertir cotizaciones cotizadas o aceptadas.');
  }

  const { createWorkOrder, addWorkOrderTask } = await import(
    '../../work-orders/infra/workOrdersRepo'
  );

  const woId = await createWorkOrder(quote.organization_id, {
    branchId: quote.branch_id,
    subjectType: 'customer',
    subjectId: null,
    // customer_user_id de la cotización es auth portal, no customers.customers.
    customerId: null,
    customerName: quote.customer_name,
    notes: `Convertida desde cotización ${quote.id.slice(0, 8).toUpperCase()}.${quote.note ? ` ${quote.note}` : ''}`,
    appointmentId: null,
  });

  // Add the quoted service as a task
  const unitPrice =
    quote.pricing_mode === 'total'
      ? (quote.quoted_total ?? 0)
      : (quote.price_per_session ?? 0);

  await addWorkOrderTask({
    workOrderId: woId,
    productId: quote.product_id,
    productName: quoteServiceDisplayName(quote.product_name),
    quantity: quote.estimated_sessions,
    unitPrice,
  });

  return woId;
}
